<template>
    <section
        ref="section"
        :class="['slice slice-hero', `slice-hero--${heroVariationModifier}`, {'slice-hero--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="hero"
    >
        <div :class="['slice-hero--with-images__shapes', {'slice-hero--with-images__shapes--with-search': props.isHeroWithSearch}]">
            <div class="slice-content text-left">
                <div class="slice-hero__content-wrapper">
                    <div class="slice-hero__content">
                        <MktBreadcrumbs v-if="breadcrumbElements.length > 0" :breadcrumbs="breadcrumbElements" />
                        <PrismicImage v-if="slice.primary.logo?.url" :field="slice.primary.logo" class="slice-hero__logo" />
                        <PrismicRichText :field="slice.primary.title" class="slice-hero__title" />
                        <div class="hero-details">
                            <PrismicRichText :field="slice.primary.description" class="slice-hero__description" />
                            <div v-if="buttons.length" class="slice-hero__ctas">
                                <div v-for="(button, idx) in buttons" :key="idx">
                                    <VJoyButton :href="button.url" :variant="button.variant" data-component-source="hero">
                                        {{ button.text }}
                                    </VJoyButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    <template v-if="!isHeroWithForm">
                        <MktDiaporama
                            v-if="[slice.primary.image, slice.primary.image2, slice.primary.image3].filter((image) => image && image.url).length > 1"
                            class="slice-hero__image"
                            :interval="7000"
                            :images="[slice.primary.image, slice.primary.image2, slice.primary.image3].filter((image) => image && image.url)"
                            :sizes="imageSizes"
                        />
                        <div v-else class="slice-hero__image">
                            <PrismicImage :field="slice.primary.image" :widths="[536, 636]" loading="eager" />
                        </div>
                    </template>
                    <template v-else>
                        <slot name="hubspot-form" />
                    </template>
                </div>
                <template v-if="isHeroWithSearch">
                    <slot name="search-panel" />
                </template>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {PrismicRichText, PrismicImage} from '@prismicio/vue';
    import type {HeroSlice, HeroSliceHeroWithImages} from '@prismicio-types';
    import {MktBreadcrumbs, MktDiaporama} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import useErrorHelpers from '../../../composables/useErrorHelpers';

    const section = ref(null);

    const props = defineProps<{
        slice: HeroSlice;
        isHeroWithSearch?: boolean;
        isHeroWithForm?: boolean;
    }>();

    const slice = computed(() => props.slice as HeroSliceHeroWithImages);

    const heroVariationModifier = computed(() => {
        if (props.isHeroWithSearch) return 'with-search';
        if (props.isHeroWithForm) return 'with-form';
        return 'with-images';
    });

    type Breadcrumb = {label: string; url?: string};

    type CtaButton = {url: string; text: string; variant: 'primary' | 'secondary' | 'main' | undefined};

    const imageSizes = 'mobile:536px tablet:636px';

    const buttons = computed<CtaButton[]>(() => {
        if (slice.value.primary?.buttons ?? []) {
            return slice.value.primary?.buttons
                ?.filter((button) => isFilled.link(button.cta))
                .map((button) => {
                    const cta = button.cta as {url: string; text?: string; variant?: 'primary' | 'secondary' | 'main'};
                    return {url: cta.url, text: cta.text ?? '', variant: cta.variant};
                });
        }
        return [];
    });

    const breadcrumbElements = computed<Breadcrumb[]>(() =>
        (slice.value.primary.items ?? [])
            .filter((item) => Boolean(item.breadscrumbText))
            .map((item) => ({
                label: item.breadscrumbText as string,
                url: isFilled.link(item.breadscrumbLink) ? (item.breadscrumbLink as {url: string}).url : undefined,
            })),
    );

    onMounted(() => {
        const {getPreviewErrorHandler, throwIfPrimaryPropertiesAreInvalid} = useErrorHelpers();
        const errorHandler = getPreviewErrorHandler();
        if (errorHandler.enabled) {
            throwIfPrimaryPropertiesAreInvalid(
                errorHandler.toast,
                props.slice.primary,
                [
                    {name: 'title', type: 'field'},
                    {name: 'description', type: 'field'},
                    {name: 'CTARequiredLink', type: 'rawText'},
                    {name: 'CTARequiredLinkText', type: 'rawText'},
                ],
                'hero',
            );
        }
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    .slice-hero {
        &--with-images {
            &__shapes {
                background-image: url('../../../../assets/svg/home-page-hero-mobile-shapes.svg');
                background-repeat: no-repeat;
                background-position: top right;
                background-size: contain;
                padding-top: 21px;

                &--with-search {
                    background-image: none;
                }

                @include mq.screen_md(min) {
                    background-image: url('../../../../assets/svg/home-page-hero-shapes.svg');
                    padding-top: 16px;
                }
            }
        }
    }
</style>
